import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingCart, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';

const Carrito = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Carrito de Compras</h1>
            <p className="text-xl opacity-90">Revisa tus estudios seleccionados</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <ShoppingCart className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Tu carrito está vacío
              </h2>
              <p className="text-gray-600 mb-6">
                Agrega estudios para comenzar tu compra
              </p>
              <Button 
                onClick={() => navigate('/estudios')}
                className="bg-[#84cc16] hover:bg-[#65a30d] text-white px-8 py-6 text-lg"
              >
                Ver Estudios Disponibles
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Carrito de Compras</h1>
          <p className="text-xl opacity-90">{getCartCount()} estudios seleccionados</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Lista de productos */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.itemId} className="shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <span className="bg-[#1e40af] text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {item.categoria}
                        </span>
                        {item.isUrgente && (
                          <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            URGENTE
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-[#1e3a8a] mb-1">
                        {item.nombre}
                      </h3>
                      <p className="text-2xl font-bold text-gray-800">
                        ${item.precio.toLocaleString('es-MX')} MXN
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      {/* Controles de cantidad */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.itemId, item.cantidad - 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-12 text-center font-semibold">{item.cantidad}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.itemId, item.cantidad + 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Botón eliminar */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(item.itemId)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-xl font-bold text-[#1e3a8a]">
                      ${(item.precio * item.cantidad).toLocaleString('es-MX')} MXN
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Resumen de compra */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg sticky top-4">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-[#1e3a8a] mb-6">Resumen de Compra</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span className="font-semibold">${getCartTotal().toLocaleString('es-MX')}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>IVA (16%):</span>
                    <span className="font-semibold">${(getCartTotal() * 0.16).toLocaleString('es-MX')}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-lg font-bold text-gray-800">Total:</span>
                    <span className="text-2xl font-bold text-[#1e3a8a]">
                      ${(getCartTotal() * 1.16).toLocaleString('es-MX')} MXN
                    </span>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg mb-3"
                >
                  Proceder al Pago
                </Button>

                <Button
                  onClick={() => navigate('/estudios')}
                  variant="outline"
                  className="w-full border-[#1e40af] text-[#1e40af] hover:bg-[#1e40af] hover:text-white"
                >
                  Seguir Comprando
                </Button>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Nota:</span> Los precios no incluyen IVA. 
                    El IVA se agregará en el checkout final.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Carrito;